// Centralized role & permission definitions and helpers

export enum Role {
  Admin = 'admin',
  SysAdmin = 'sys_admin',
  OpsDirector = 'ops_director',
  OpsSupervisor = 'ops_supervisor',
  RegionalManager = 'regional_manager',
  StoreManager = 'store_manager',
  DeviceOps = 'device_ops',
  Operator = 'operator',
  Viewer = 'viewer',
}

export enum Permission {
  ReportsView = 'reports:view',
  ReportsExport = 'reports:export',
  FloorplanEdit = 'floorplan:edit',
  DevicesManage = 'devices:manage',
  UsersManage = 'users:manage',
  SystemSettings = 'system:settings',
  TasksCreate = 'tasks:create',
  TasksAssign = 'tasks:assign',
  TasksUpdate = 'tasks:update',
  TasksClose = 'tasks:close',
}

// Label display for UI
export const ROLE_LABELS: Record<Role, string> = {
  [Role.Admin]: '管理员',
  [Role.SysAdmin]: '系统管理员',
  [Role.OpsDirector]: '运营总监',
  [Role.OpsSupervisor]: '运营主管',
  [Role.RegionalManager]: '区域经理',
  [Role.StoreManager]: '店长',
  [Role.DeviceOps]: '设备运维',
  [Role.Operator]: '操作员',
  [Role.Viewer]: '观察员',
};

export type ScopeType = 'global' | 'region' | 'store' | 'none';

export const ROLE_SCOPE: Record<Role, ScopeType> = {
  [Role.Admin]: 'global',
  [Role.SysAdmin]: 'global',
  [Role.OpsDirector]: 'global',
  [Role.OpsSupervisor]: 'region',
  [Role.RegionalManager]: 'region',
  [Role.StoreManager]: 'store',
  [Role.DeviceOps]: 'global',
  [Role.Operator]: 'store',
  [Role.Viewer]: 'none',
};

// Permission sets per role (can be extended or replaced by backend)
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [Role.Admin]: Object.values(Permission),
  [Role.SysAdmin]: Object.values(Permission),
  [Role.OpsDirector]: [
    Permission.ReportsView,
    Permission.ReportsExport,
    Permission.TasksCreate,
    Permission.TasksAssign,
    Permission.TasksUpdate,
    Permission.TasksClose,
    Permission.FloorplanEdit,
  ],
  [Role.OpsSupervisor]: [
    Permission.ReportsView,
    Permission.TasksCreate,
    Permission.TasksAssign,
    Permission.TasksUpdate,
    Permission.TasksClose,
  ],
  [Role.RegionalManager]: [
    Permission.ReportsView,
    Permission.TasksCreate,
    Permission.TasksAssign,
    Permission.TasksUpdate,
  ],
  [Role.StoreManager]: [
    Permission.ReportsView,
    Permission.TasksUpdate,
  ],
  [Role.DeviceOps]: [
    Permission.DevicesManage,
  ],
  [Role.Operator]: [
    Permission.ReportsView,
    Permission.TasksUpdate,
  ],
  [Role.Viewer]: [
    Permission.ReportsView,
  ],
};

// Helper methods
export function isGlobalRole(role?: string): boolean {
  if (!role) return false;
  return [Role.Admin, Role.SysAdmin, Role.OpsDirector, Role.DeviceOps].includes(role as Role);
}

export function hasRole(userRoles: string[] = [], roles: Role[] = []): boolean {
  if (!roles.length) return true;
  return roles.some(r => userRoles.includes(r));
}

export function hasPermission(userPerms: string[] = [], perm: Permission): boolean {
  return userPerms.includes(perm);
}

export function canAccessStore(userRoles: string[] = [], assignedStoreIds: string[] = [], storeId?: string): boolean {
  if (!storeId) return true;
  if (hasRole(userRoles, [Role.Admin, Role.SysAdmin, Role.OpsDirector])) return true;
  if (!assignedStoreIds.length) return true;
  return assignedStoreIds.includes(storeId);
}

export function canAccessRegion(userRoles: string[] = [], assignedRegionIds: string[] = [], regionId?: string): boolean {
  if (!regionId) return true;
  if (hasRole(userRoles, [Role.Admin, Role.SysAdmin, Role.OpsDirector])) return true;
  if (!assignedRegionIds.length) return true;
  return assignedRegionIds.includes(regionId);
}